/**
 * React Query bindings for tools. Reads → `useTool(name, input)`; writes → `useToolMutation(name)`
 * which goes through `runTool` (confirmation modal + human-approval toast) and refreshes every tool
 * query on success. Pass `input = null` to keep a query disabled (e.g. no brand selected yet).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { ApiError, callTool, errorMessage, isCancelled, runTool, type RunToolOptions } from "./client";
import { toast } from "./toast-store";
import type { ToolName } from "./tools";

export type ToolInput = Record<string, unknown>;

export function toolKey(name: string, input?: ToolInput | null) {
  return input === undefined ? (["tool", name] as const) : (["tool", name, input ?? null] as const);
}

export interface UseToolOptions<T> {
  enabled?: boolean;
  refetchInterval?: number | false | ((data: T | undefined) => number | false);
  staleTime?: number;
  keepPrevious?: boolean;
  retry?: number;
}

export function useTool<T = unknown>(name: ToolName, input: ToolInput | null, opts: UseToolOptions<T> = {}) {
  const options: UseQueryOptions<T, Error, T, readonly unknown[]> = {
    queryKey: toolKey(name, input),
    queryFn: ({ signal }) => callTool<T>(name, input ?? {}, { signal }),
    enabled: input !== null && opts.enabled !== false,
    staleTime: opts.staleTime ?? 15_000,
    placeholderData: opts.keepPrevious ? keepPreviousData : undefined,
    retry: (count, err) => {
      if (err instanceof ApiError && (err.status >= 400 && err.status < 500)) return false;
      if (err instanceof ApiError && err.isNetwork) return count < 1;
      return count < (opts.retry ?? 1);
    },
    refetchInterval:
      typeof opts.refetchInterval === "function"
        ? (q) => (opts.refetchInterval as (d: T | undefined) => number | false)(q.state.data as T | undefined)
        : opts.refetchInterval,
  };
  return useQuery(options);
}

export interface UseToolMutationOptions<T> extends RunToolOptions {
  /** Toast on success (string or fn of result). */
  success?: string | ((result: T) => string | null);
  /** Suppress the default error toast. */
  silentError?: boolean;
  onSuccess?: (result: T, input: ToolInput) => void;
  onError?: (err: unknown) => void;
}

export function useToolMutation<T = unknown>(name: ToolName | string, opts: UseToolMutationOptions<T> = {}) {
  const qc = useQueryClient();
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  return useMutation<T, unknown, ToolInput>({
    mutationKey: ["tool-mutation", name],
    mutationFn: (input) => runTool<T>(name, input, { label: opts.label }),
    onSuccess: (result, input) => {
      void qc.invalidateQueries({ queryKey: ["tool"] });
      if (!mounted.current) return;
      const msg = typeof opts.success === "function" ? opts.success(result) : opts.success;
      if (msg) toast({ tone: "success", title: msg });
      opts.onSuccess?.(result, input);
    },
    onError: (err) => {
      if (!mounted.current || isCancelled(err)) return;
      if (!(err instanceof ApiError && err.code === "human_approval_required") && !opts.silentError) {
        toast({ tone: "danger", title: `${humanTool(name)} failed`, body: errorMessage(err) });
      }
      opts.onError?.(err);
    },
  });
}

/** Imperative interactive tool runner for dynamic tool names (recommendation actions, tool runner). */
export function useRunTool() {
  const qc = useQueryClient();
  return useCallback(
    async <T = unknown>(name: string, input: ToolInput, opts: RunToolOptions & { success?: string; silentError?: boolean } = {}): Promise<T | undefined> => {
      try {
        const result = await runTool<T>(name, input, opts);
        void qc.invalidateQueries({ queryKey: ["tool"] });
        if (opts.success) toast({ tone: "success", title: opts.success });
        return result;
      } catch (err) {
        if (isCancelled(err)) return undefined;
        if (!(err instanceof ApiError && err.code === "human_approval_required") && !opts.silentError) {
          toast({ tone: "danger", title: `${humanTool(name)} failed`, body: errorMessage(err) });
        }
        throw err;
      }
    },
    [qc],
  );
}

/** "creatives.regenerate_asset" → "Regenerate asset" */
export function humanTool(name: string): string {
  const verb = name.includes(".") ? name.slice(name.indexOf(".") + 1) : name;
  const s = verb.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}
